<template>
  <div class="container">
    <div class="pb-128 pt-80">
      <div
        class="animated-section"
        :class="{ 'animated-section--reversed': blok.reversed_order }"
      >
        <div class="animated-section__img-wrapper">
          <div ref="stickyImage" class="animated-section__img">
            <StoryblokComponent
              v-if="currentImage"
              :key="currentImage._uid"
              :blok="currentImage"
            />
          </div>
        </div>
        <div class="animated-section__scroll-content">
          <div
            v-for="(childblok, index) in blok.content_desktop"
            :key="index"
            ref="contentSections"
            class="animated-section__content-wrapper"
            :class="{
              'animated-section__content-wrapper--inactive':
                !isSectionActive(index),
            }"
            :data-index="index"
          >
            <StoryblokComponent :blok="childblok" />
          </div>
        </div>
      </div>
      <div class="animated-section__mobile">
        <StoryblokComponent
          v-for="childBlok in blok.content_mobile"
          :key="childBlok._uid"
          :blok="childBlok"
        />
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { gsap } from 'gsap'
import { ScrollTrigger } from 'gsap/ScrollTrigger'
import type { SbSectionAnimatedScroll, SbPictureStoryblok } from '@/types'
import { checkIfElementIsInViewport } from '@/utils/helpers'

gsap.registerPlugin(ScrollTrigger)

interface Props {
  blok: SbSectionAnimatedScroll
}
const props = defineProps<Props>()

const currentImage = ref<SbPictureStoryblok | undefined>(
  props.blok.illustrations?.[0],
)

const nextImage = ref<SbPictureStoryblok>()
const stickyImage = ref<HTMLElement | null>(null)
const contentSections = ref<HTMLElement[]>([])
const activeSectionIndex = ref(0)
const scrollTriggers = ref<ScrollTrigger[]>([])

const prepareImageChange = (index: number) => {
  if (!props.blok.illustrations) {
    return
  }
  const newImage = props.blok.illustrations[index]
  if (currentImage.value !== newImage) {
    nextImage.value = newImage
    animateImageChange()
  }
  activeSectionIndex.value = index
}

const animateImageChange = () => {
  const fadeOut = {
    opacity: 0,
    ease: 'power1.out',
    duration: 0.2,
  }
  const scaleDown = {
    scale: 0.99,
    duration: 0.0001,
  }

  const scaleUp = {
    scale: 1,
    opacity: 1,
    ease: 'power1.in',
    duration: 0.2,
  }

  const tl = gsap.timeline()
  tl.to(stickyImage.value, fadeOut)
    .set(stickyImage.value, {
      onComplete: () => {
        currentImage.value = nextImage.value
      },
    })
    .to(stickyImage.value, scaleDown)
    .to(stickyImage.value, scaleUp)
}

const isSectionActive = (index: number) => {
  return index === activeSectionIndex.value
}

const updateTriggers = () => {
  const imgWrapper = stickyImage.value?.closest(
    '.animated-section__img-wrapper',
  )
  if (!imgWrapper) {
    return
  }
  if (!checkIfElementIsInViewport(imgWrapper as HTMLElement, true)) {
    return
  }
  const imgWrapperBottomPosition = imgWrapper.getBoundingClientRect().bottom
  const offsetFromBottom = window.innerHeight - imgWrapperBottomPosition
  const startTrigger = `bottom bottom-=${offsetFromBottom}`

  scrollTriggers.value.forEach((trigger: ScrollTrigger) => {
    trigger.vars.start = startTrigger
    trigger.refresh()
  })
}

const createScrollTriggers = () => {
  if (!contentSections.value) return

  scrollTriggers.value = gsap.utils
    .toArray<HTMLElement>(contentSections.value)
    .map((section: HTMLElement, index: number) => {
      return ScrollTrigger.create({
        trigger: section,
        scrub: true,
        start: `bottom bottom`,
        end: `top top+=140`,
        onEnter: () => prepareImageChange(index),
        onEnterBack: () => prepareImageChange(index),
      })
    })
}

onMounted(() => {
  createScrollTriggers()
  window.addEventListener('resize', updateTriggers)
  window.addEventListener('scroll', updateTriggers)
})
onUnmounted(() => {
  window.removeEventListener('resize', updateTriggers)
  window.removeEventListener('scroll', updateTriggers)
})
</script>

<style scoped lang="scss">
.animated-section {
  display: none;
  @media (min-width: $breakpoint-lg) {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;

    &--reversed {
      flex-direction: row-reverse;
    }

    &__img-wrapper {
      position: sticky;
      top: 150px;
      width: 573px;
      height: 573px;
      background-color: var(--cobalt-200);
      border-radius: 20px;
      overflow: hidden;
    }
    &__img {
      height: 100%;
      display: flex;
    }
    &__content-wrapper {
      display: flex;
      justify-content: center;
      flex-direction: column;
      margin-bottom: 280px;
      width: 316px;
      opacity: 1;
      transition: opacity 0.2s ease-in-out;
      &--inactive {
        opacity: 0.3;
      }
      &:first-child {
        margin-top: 180px;
      }
      &:last-child {
        margin-bottom: 200px;
      }
    }
  }
  &__mobile {
    display: block;
    @media (min-width: $breakpoint-lg) {
      display: none;
    }
  }
}
</style>
